import type { Connection, HassEntities } from 'home-assistant-js-websocket';
import type { Area, Device, Entity, Floor, RegistryState } from './types';

interface FloorRegistryEntry {
  floor_id: string;
  name: string;
  level: number | null;
  icon?: string;
}

interface AreaRegistryEntry {
  area_id: string;
  name: string;
  floor_id: string | null;
  icon?: string | null;
}

interface DeviceRegistryEntry {
  id: string;
  name: string | null;
  name_by_user?: string | null;
  area_id?: string | null;
  model?: string | null;
  manufacturer?: string | null;
}

interface EntityRegistryEntry {
  entity_id: string;
  device_id?: string | null;
  area_id?: string | null;
  labels?: string[];
  name?: string | null;
  original_name?: string | null;
}

interface LabelRegistryEntry {
  label_id: string;
  name?: string;
}

export type RegistryUpdateType =
  | 'floor_registry_updated'
  | 'area_registry_updated'
  | 'device_registry_updated'
  | 'entity_registry_updated'
  | 'label_registry_updated'
  | string;

export interface RegistryUpdateEvent {
  event_type: string;
}

export interface RegistryClient {
  loadAll(): Promise<RegistryState>;
  subscribeUpdates(onUpdate: (eventType: string) => void): Promise<() => void>;
}

const REGISTRY_EVENTS = [
  'floor_registry_updated',
  'area_registry_updated',
  'device_registry_updated',
  'entity_registry_updated',
  'label_registry_updated',
];

export function createRegistryClient(conn: Connection): RegistryClient {
  async function loadAll(): Promise<RegistryState> {
    const [floorsRaw, areasRaw, devicesRaw, entitiesRaw, labelsRaw, statesMap] = await Promise.all([
      conn.sendMessagePromise<FloorRegistryEntry[]>({ type: 'config/floor_registry/list' }),
      conn.sendMessagePromise<AreaRegistryEntry[]>({ type: 'config/area_registry/list' }),
      conn.sendMessagePromise<DeviceRegistryEntry[]>({ type: 'config/device_registry/list' }),
      conn.sendMessagePromise<EntityRegistryEntry[]>({ type: 'config/entity_registry/list' }),
      conn.sendMessagePromise<LabelRegistryEntry[]>({ type: 'config/label_registry/list' }),
      fetchStates(conn),
    ]);

    const floors: Floor[] = floorsRaw.map((entry) => ({
      floor_id: entry.floor_id,
      name: entry.name,
      level: entry.level ?? 0,
    }));

    const areas: Area[] = areasRaw
      .filter((entry): entry is AreaRegistryEntry & { floor_id: string } => !!entry.floor_id)
      .map((entry) => ({
        area_id: entry.area_id,
        name: entry.name,
        floor_id: entry.floor_id,
        icon: entry.icon ?? undefined,
      }));

    const devices: Device[] = devicesRaw.map((entry) => ({
      device_id: entry.id,
      name: entry.name_by_user ?? entry.name ?? entry.id,
      area_id: entry.area_id ?? undefined,
      model: entry.model ?? undefined,
      manufacturer: entry.manufacturer ?? undefined,
    }));

    const entities: Entity[] = entitiesRaw.map((entry) => {
      const state = statesMap?.[entry.entity_id];
      return {
        entity_id: entry.entity_id,
        device_id: entry.device_id ?? undefined,
        area_id: entry.area_id ?? undefined,
        state: state?.state ?? 'unknown',
        attributes: state?.attributes ?? {},
        last_changed: state?.last_changed ?? '',
        labels: entry.labels ?? [],
      };
    });

    const labels = labelsRaw.map((entry) => entry.label_id);

    return { floors, areas, devices, entities, labels, ready: true };
  }

  async function subscribeUpdates(onUpdate: (eventType: string) => void): Promise<() => void> {
    const unsubs: Array<() => Promise<void>> = [];
    for (const eventType of REGISTRY_EVENTS) {
      const unsub = await conn.subscribeEvents<RegistryUpdateEvent>(() => {
        onUpdate(eventType);
      }, eventType);
      unsubs.push(unsub);
    }
    return () => {
      for (const unsub of unsubs) {
        void unsub();
      }
    };
  }

  return { loadAll, subscribeUpdates };
}

async function fetchStates(conn: Connection): Promise<HassEntities | undefined> {
  try {
    const states = await conn.sendMessagePromise<
      Array<{ entity_id: string; state: string; attributes: Record<string, unknown>; last_changed: string }>
    >({ type: 'get_states' });
    const map: HassEntities = {};
    for (const s of states) {
      map[s.entity_id] = {
        entity_id: s.entity_id,
        state: s.state,
        attributes: s.attributes,
        last_changed: s.last_changed,
        last_updated: s.last_changed,
        context: { id: '', parent_id: null, user_id: null },
      };
    }
    return map;
  } catch {
    return undefined;
  }
}
